import React from 'react';
import { Row, Col, Label, Input, FormFeedback } from 'reactstrap';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { HelpTextHover } from '../Tooltip';
import { PlusLg, DashLg } from 'react-bootstrap-icons';
import ReactSlider from 'react-slider';
import { formatToNumber } from '../../utils/Format';

/**
 * Unified input component that supports multiple display modes
 * Modes: 'number', 'currency', 'percentage'
 * Controls: 'slider', 'arrows'
 */
export const UnifiedInputControl = ({
    id,
    label,
    value,
    isInvalid,
    onChange,
    onFocus,
    onBlur,
    
    // Control type configuration
    mode = 'number',           // 'number' | 'currency' | 'percentage'
    controlType = 'slider',    // 'slider' | 'arrows'
    
    // Slider configuration
    onSliderChange,
    min = 0,
    max = 100,
    step = 1,
    
    // Arrow configuration
    onIncrement,
    onDecrement,
    incrementAmount = 1,
    
    // Optional features
    tooltipId,
    tooltipText,
    visible = true
}) => {
    if (!visible) return null;

    // Determine input type and class based on mode
    const inputType = mode === 'currency' ? 'text' : 'number';
    const inputClass = mode === 'currency' ? 'dollarPrependInput' : 
                      mode === 'percentage' ? 'percentAppendInput' : '';

    // Get numeric value for slider (handles formatted currency strings)
    const getNumericValue = () => {
        if (mode === 'currency' && typeof value === 'string') {
            return formatToNumber(value);
        }
        return value || 0;
    };

    // Render prefix for currency
    const renderPrefix = () => {
        if (mode !== 'currency') return null;
        return (
            <div className="input-group-prepend">
                <div className="input-group-text dollarPrepend">$</div>
            </div>
        );
    };

    // Render suffix for percentage
    const renderSuffix = () => {
        if (mode !== 'percentage') return null;
        return (
            <div className="input-group-append">
                <span className="input-group-text percentAppend">%</span>
            </div>
        );
    };

    // Render slider control
    const renderSlider = () => {
        if (controlType !== 'slider') return null;
        return (
            <ReactSlider
                onChange={onSliderChange}
                value={getNumericValue()}
                renderThumb={(props, state) => <div {...props}>{state.valueNow.toLocaleString()}</div>}
                step={step}
                max={max}
                min={min}
            />
        );
    };

    // Render arrow controls
    const renderArrows = () => {
        if (controlType !== 'arrows') return null;
        
        const formatAmount = () => {
            if (mode === 'currency') return `$${incrementAmount.toLocaleString()}`;
            if (mode === 'percentage') return `${incrementAmount}%`;
            return incrementAmount;
        };

        return (
            <div className="upDownWrapper">
                <div className="arrowWrapper">
                    <span title={`Increase by ${formatAmount()}`} onClick={onIncrement}>
                        <PlusLg height={35} width={60} color="#ddd" />
                    </span>
                </div>
                <div className="arrowWrapper">
                    <span title={`Decrease by ${formatAmount()}`} onClick={onDecrement}>
                        <DashLg height={35} width={60} color="#ddd" />
                    </span>
                </div>
            </div>
        );
    };

    return (
        <Row>
            <Col xs="8">
                <Label for={id}>
                    {label}
                </Label>
                {tooltipText && (
                    <>
                        <span data-tooltip-id={tooltipId}><HelpTextHover /></span>
                        <ReactTooltip id={tooltipId} aria-haspopup="true">{tooltipText}</ReactTooltip>
                    </>
                )}
                <div className="input-group">
                    {renderPrefix()}
                    <Input
                        id={id}
                        type={inputType}
                        className={inputClass}
                        value={value}
                        invalid={isInvalid}
                        onChange={onChange}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        step={mode === 'number' ? step : undefined}
                    />
                    {renderSuffix()}
                    <FormFeedback>Please enter a valid number.</FormFeedback>
                </div>
            </Col>
            <Col>
                {renderSlider()}
                {renderArrows()}
            </Col>
        </Row>
    );
};